/**
 * WANLE-SBX-1 · 渠道中心 › 携程玩乐 › 「沙箱测试」卡（价格推送卡下面）。
 * 携程沙箱一次只测一个用例、只认测试产品登记的日期：这里登记测试日期（≤5）→ 自动推送暂停；再用 4 个按钮逐个用例手动推（价格 / 库存 × 单个日期 / 全部测试日期），结果就地显示携程原话。
 * 🔴 正式上线前必须「退出沙箱模式」，否则自动推送一直暂停。
 *   PUT  /api/v1/integrations/platforms/ctrip_wanle/wanle-sandbox
 *   POST /api/v1/integrations/platforms/ctrip_wanle/wanle-sandbox:push
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/services/api";
import { wanleApi, type WanleSandboxResult } from "@/services/ctripWanle";
import { listMappings, MAPPINGS_QUERY_KEY } from "@/services/channelCenter";

const inp = "rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 font-mono text-[13px]";
const btn = "rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40";

const ACTIONS = [
  ["price", "one", "推价格 · 单个日期"], ["price", "all", "推价格 · 两个日期"],
  ["inventory", "one", "推库存 · 单个日期"], ["inventory", "all", "推库存 · 两个日期"],
] as const;

export function WanleSandboxCard({ dates, canEdit, queryKeys }: { dates: string[]; canEdit: boolean; queryKeys: unknown[][] }) {
  const qc = useQueryClient();
  const sandbox = dates.length > 0;
  const [draft, setDraft] = useState<string[]>(dates.length ? dates : [""]);
  const [err, setErr] = useState<string | null>(null);
  const [mappingId, setMappingId] = useState("");
  const [result, setResult] = useState<(WanleSandboxResult & { label: string }) | null>(null);
  const [pushErr, setPushErr] = useState<string | null>(null);
  useEffect(() => { setDraft(dates.length ? dates : [""]); }, [dates]);

  const mapsQ = useQuery({ queryKey: [...MAPPINGS_QUERY_KEY, "ctrip_wanle", "sandbox"], queryFn: () => listMappings("ctrip_wanle"), enabled: sandbox });
  const options = (mapsQ.data ?? []).flatMap((r) => (r.wanle_bindings ?? []).filter((b) => b.bound && b.mapping_id).map((b) => ({ id: b.mapping_id!, plu: b.plu, name: r.product_name })));
  useEffect(() => { if (!mappingId && options.length) setMappingId(options[0].id); }, [options, mappingId]);

  const refresh = () => queryKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
  const save = useMutation({
    mutationFn: (ds: string[]) => wanleApi.saveSandbox(ds),
    onSuccess: () => { setErr(null); setResult(null); setPushErr(null); refresh(); qc.invalidateQueries({ queryKey: MAPPINGS_QUERY_KEY }); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "保存失败，请稍后再试"),
  });
  const push = useMutation({
    mutationFn: (a: { kind: "price" | "inventory"; scope: "one" | "all"; label: string }) => wanleApi.sandboxPush({ mapping_id: mappingId, kind: a.kind, scope: a.scope }).then((r) => ({ ...r, label: a.label })),
    onMutate: () => { setResult(null); setPushErr(null); },
    onSuccess: (r) => setResult(r),
    onError: (e) => setPushErr(e instanceof ApiError ? e.message : "推送失败，请稍后再试"),
  });
  const cleaned = draft.map((d) => d.trim()).filter(Boolean);

  return (
    <section data-testid="wanle-sandbox" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="mb-1 text-sm font-semibold text-slate-900">沙箱测试</h2>
      {sandbox && (
        <div data-testid="wanle-sandbox-banner" role="status" className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] font-semibold text-amber-800">
          沙箱测试中 · 自动推送已暂停 · 正式上线前必须退出
        </div>
      )}
      <p className="mb-3 text-[12px] text-slate-500">携程沙箱一次只测一个用例，且只认测试产品里登记的日期。登记测试日期后，系统自动推送（库存 / 价格 / 对账补推）全部暂停，由你在下面逐个用例手动推。</p>
      <div className="mb-2 text-[12.5px] text-slate-600">测试日期（1–5 个，yyyy-MM-dd）</div>
      <div className="flex flex-wrap items-center gap-2">
        {draft.map((d, i) => (
          <input key={i} aria-label={`测试日期 ${i + 1}`} data-testid={`wanle-sbx-date-${i}`} type="date" value={d} disabled={!canEdit}
            onChange={(e) => setDraft((x) => x.map((v, j) => (j === i ? e.target.value : v)))} className={inp} />
        ))}
        {canEdit && draft.length < 5 && <button type="button" data-testid="wanle-sbx-add" onClick={() => setDraft((x) => [...x, ""])} className={btn}>+ 加一个日期</button>}
        {canEdit && <button type="button" data-testid="wanle-sbx-save" disabled={!cleaned.length || save.isPending} onClick={() => save.mutate(cleaned)} className="rounded-md bg-blue-600 px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-40">{sandbox ? "更新测试日期" : "进入沙箱模式"}</button>}
        {canEdit && sandbox && <button type="button" data-testid="wanle-sbx-exit" disabled={save.isPending} onClick={() => save.mutate([])} className="rounded-md border border-rose-300 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-rose-700 hover:bg-rose-50">退出沙箱模式</button>}
      </div>
      {err && <div role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{err}</div>}

      {sandbox && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-[12.5px] text-slate-600">
            <span>选择映射（PLU）</span>
            <select aria-label="选择映射" data-testid="wanle-sbx-mapping" value={mappingId} onChange={(e) => setMappingId(e.target.value)} className={inp}>
              {options.length === 0 && <option value="">（还没有已绑定的映射）</option>}
              {options.map((o) => <option key={o.id} value={o.id}>{o.plu} · {o.name}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            {ACTIONS.map(([kind, scope, label]) => (
              <button key={label} type="button" data-testid={`wanle-sbx-${kind}-${scope}`} disabled={!canEdit || !mappingId || push.isPending} onClick={() => push.mutate({ kind, scope, label })} className={btn}>{label}</button>
            ))}
          </div>
          {push.isPending && <div className="mt-2 text-[12.5px] text-slate-500">发送中…</div>}
          {pushErr && <div data-testid="wanle-sbx-error" role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{pushErr}</div>}
          {result && (
            <div data-testid="wanle-sbx-result" className={`mt-2 rounded-md border px-3 py-2 text-[12.5px] ${result.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-700"}`}>
              <div className="font-semibold">{result.label} · {result.ok ? "成功" : "携程拒收"}</div>
              <div>接口 {result.service} · HTTP {result.http_status ?? "—"} · resultCode {result.result_code || "—"}{result.ok ? "" : ` · ${result.result_message}`}</div>
              <div>推了这几天：{result.sent_dates.join("、")}</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
